import { useState } from 'react'
import { ExternalLink, Lock, Unlock, Download, Play, Flag, RotateCcw } from 'lucide-react'
import { clsx } from 'clsx'
import {
  useFetchGameweekStats,
  useLockGameweek,
  useProcessGameweek,
  useFinalizeGameweek,
  useResetGameweek,
  useReopenTransferWindow,
  type AdminGameweek,
  type AdminAction,
} from '../queries'
import { ConfirmDestructiveModal } from '@/shared/components/ui/ConfirmDestructiveModal'
import styles from './GameweekActionsCard.module.css'

type GameweekActionsCardProps = {
  gw: AdminGameweek
}

export function GameweekActionsCard({ gw }: GameweekActionsCardProps) {
  const [lastAction, setLastAction] = useState<AdminAction | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  const [confirmReopen, setConfirmReopen] = useState(false)

  const lock     = useLockGameweek()
  const fetchM   = useFetchGameweekStats()
  const process  = useProcessGameweek()
  const finalize = useFinalizeGameweek()
  const reset    = useResetGameweek()
  const reopen   = useReopenTransferWindow()

  const anyPending = lock.isPending || fetchM.isPending || process.isPending
                  || finalize.isPending || reset.isPending || reopen.isPending

  const isPastRound = new Date(gw.lastMatchEndsAt) < new Date()

  // State machine (see .claude/plans/admin-manual.md):
  //   UPCOMING ─(prev GW finalized)─> ACTIVE ─(Lock)─> LOCKED ─(Process: scores)─┐
  //                          ▲                            │ (still LOCKED)        │
  //                          └──────────(Reset)───────────┴─(Finalize: standings)─> FINALIZED
  // Process writes scores but leaves the GW LOCKED; Finalize publishes standings
  // and closes the round. Reset undoes the last step (Process or Finalize).
  const canLock     = gw.status === 'ACTIVE'
  const canFetch    = gw.status === 'LOCKED'
                   || gw.status === 'FINALIZED'
                   || ((gw.status === 'ACTIVE' || gw.status === 'UPCOMING') && isPastRound)
  const canProcess  = gw.status === 'LOCKED'
                   || ((gw.status === 'ACTIVE' || gw.status === 'UPCOMING') && isPastRound)
  const canFinalize = gw.status === 'LOCKED' && gw.scoresComputed
  // Reset is recompute-only: it deletes this GW's scores and returns FINALIZED → LOCKED
  // (or stays LOCKED), keeping the roster frozen so it can be re-Processed. Valid from
  // any LOCKED or FINALIZED GW.
  const canReset    = gw.status === 'FINALIZED' || gw.status === 'LOCKED'
  // Reopen reopens the transfer window (LOCKED → ACTIVE) so users can pick again. Only
  // from LOCKED — a finalized GW must be reset first. Backend 409s otherwise.
  const canReopen   = gw.status === 'LOCKED'

  function handleLock() {
    lock.mutate(gw.id, {
      onSuccess: (result) => setLastAction({ kind: 'lock', result }),
    })
  }

  function handleFetch() {
    fetchM.mutate(gw.id, {
      onSuccess: (result) => setLastAction({ kind: 'fetch', result }),
    })
  }

  function handleProcess() {
    process.mutate(gw.id, {
      onSuccess: (result) => setLastAction({ kind: 'process', result }),
    })
  }

  function handleFinalize() {
    finalize.mutate(gw.id, {
      onSuccess: (result) => setLastAction({ kind: 'finalize', result }),
    })
  }

  function handleResetConfirm() {
    reset.mutate(gw.id, {
      onSuccess: (result) => {
        setLastAction({ kind: 'reset', result })
        setConfirmReset(false)
      },
    })
  }

  function handleReopenConfirm() {
    reopen.mutate(gw.id, {
      onSuccess: (result) => {
        setLastAction({ kind: 'reopen', result })
        setConfirmReopen(false)
      },
    })
  }

  return (
    <>
      <div className={styles.card}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>
              GW{gw.number} — <span className={styles.phase}>{gw.phase}</span>
            </h2>
            <p className={styles.subtitle}>
              <span className={clsx(styles.statusBadge, styles[`status_${gw.status}`])}>
                {gw.status}
              </span>
              <span className={styles.subtitleSep}>·</span>
              Primeiro jogo: {formatDateTime(gw.firstMatchAt)}
            </p>
          </div>
          <a
            href="/hangfire"
            target="_blank"
            rel="noreferrer"
            className={styles.hangfireLink}
          >
            Hangfire <ExternalLink size={12} />
          </a>
        </header>

        {isPastRound && (gw.status === 'UPCOMING' || gw.status === 'ACTIVE') && (
          <p className={styles.pastNotice}>
            Rodada encerrada — use <strong>Fetch</strong> para buscar stats, depois{' '}
            <strong>Process</strong> e <strong>Finalize</strong>. Use <strong>Lock</strong> antes
            se precisar capturar baselines de transferências.
          </p>
        )}

        <div className={styles.actions}>
          <ActionButton
            icon={<Lock size={14} />}
            label="Lock"
            hint={isPastRound ? "Bloqueia escalações e cria baselines (sem agendamento automático)" : "Bloqueia escalações e agenda processamento"}
            disabled={!canLock || anyPending}
            isPending={lock.isPending}
            onClick={handleLock}
          />
          <ActionButton
            icon={<Download size={14} />}
            label="Fetch stats"
            hint="Busca stats das partidas no API-Football"
            disabled={!canFetch || anyPending}
            isPending={fetchM.isPending}
            onClick={handleFetch}
          />
          <ActionButton
            icon={<Play size={14} />}
            label="Process"
            hint="Calcula os scores (não finaliza a rodada)"
            disabled={!canProcess || anyPending}
            isPending={process.isPending}
            onClick={handleProcess}
          />
          <ActionButton
            icon={<Flag size={14} />}
            label="Finalize"
            hint="Publica standings e finaliza a rodada"
            disabled={!canFinalize || anyPending}
            isPending={finalize.isPending}
            onClick={handleFinalize}
          />
          <ActionButton
            icon={<RotateCcw size={14} />}
            label="Reset"
            hint="Recalcular: apaga scores e volta para LOCKED (roster congelado) — desfaz Process/Finalize"
            disabled={!canReset || anyPending}
            isPending={reset.isPending}
            onClick={() => setConfirmReset(true)}
            variant="danger"
          />
          <ActionButton
            icon={<Unlock size={14} />}
            label="Reabrir janela"
            hint="Reabre a janela de transferências (ACTIVE) para os usuários reescalarem"
            disabled={!canReopen || anyPending}
            isPending={reopen.isPending}
            onClick={() => setConfirmReopen(true)}
            variant="danger"
          />
        </div>

        {lastAction && <LastActionPanel action={lastAction} />}
      </div>

      {confirmReset && (
        <ConfirmDestructiveModal
          title="Resetar (recalcular) GW?"
          body={
            'Isso apaga os squad scores e player scores desta rodada para que ela possa ser ' +
            'reprocessada com o MESMO roster congelado (snapshot).\n\n' +
            'A rodada volta para LOCKED — escalações continuam bloqueadas e a janela fechada. ' +
            'Se já estava finalizada, standings e totais também são revertidos.\n\n' +
            'Para deixar os usuários reescalarem, use "Reabrir janela" depois.'
          }
          confirmText={`Resetar GW${gw.number}`}
          isPending={reset.isPending}
          onConfirm={handleResetConfirm}
          onCancel={() => setConfirmReset(false)}
        />
      )}

      {confirmReopen && (
        <ConfirmDestructiveModal
          title="Reabrir janela de transferências?"
          body={
            'Isso reabre a janela desta rodada (volta para ACTIVE) e libera as escalações de ' +
            'todos os participantes para edição. Os créditos consumidos no lock desta rodada ' +
            'são restaurados.\n\n' +
            'O snapshot atual é mantido — ao bloquear novamente, uma nova versão é gravada e ' +
            'passa a valer para a pontuação.'
          }
          confirmText={`Reabrir GW${gw.number}`}
          isPending={reopen.isPending}
          onConfirm={handleReopenConfirm}
          onCancel={() => setConfirmReopen(false)}
        />
      )}
    </>
  )
}

type ActionButtonProps = {
  icon:      React.ReactNode
  label:     string
  hint:      string
  disabled:  boolean
  isPending: boolean
  onClick:   () => void
  variant?:  'default' | 'danger'
}

function ActionButton({ icon, label, hint, disabled, isPending, onClick, variant = 'default' }: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(styles.actionBtn, variant === 'danger' && styles.actionBtnDanger)}
      title={hint}
    >
      <span className={styles.actionIcon}>
        {isPending ? <span className={styles.spinner} aria-hidden="true" /> : icon}
      </span>
      <span className={styles.actionLabelWrap}>
        <span className={styles.actionLabel}>{label}</span>
        <span className={styles.actionHint}>{hint}</span>
      </span>
    </button>
  )
}

function LastActionPanel({ action }: { action: AdminAction }) {
  return (
    <div className={styles.lastAction}>
      <div className={styles.lastActionHeader}>
        Última ação: <strong>{ACTION_LABEL[action.kind]}</strong>
      </div>
      <pre className={styles.lastActionBody}>
        {action.kind === 'lock'
          ? 'OK — squads bloqueados.'
          : JSON.stringify(action.result, null, 2)}
      </pre>
    </div>
  )
}

const ACTION_LABEL: Record<AdminAction['kind'], string> = {
  lock:     'Lock',
  fetch:    'Fetch stats',
  process:  'Process',
  finalize: 'Finalize',
  reset:    'Reset',
  reopen:   'Reabrir janela',
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', {
    day:    '2-digit',
    month:  '2-digit',
    year:   '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  })
}
